<script>
export default {
  name: "CrmRewardCard",
  props: {
    item: {
      type: Object,
      required: true
    },
    point: {
      type: Number,
      default: 0
    },
    outOfStock: {
      type: Boolean,
      default: false
    },
    dataTest: {
      type: String,
      required: true
    }
  },
  emits: ["select"],
  computed: {
    image() {
      return this.item.image_url || this.handleImageSrc;
    },
    progressValue() {
      if (!this.item.point_used) return 0;
      return Math.min(this.point, this.item.point_used);
    },
    progressPercent() {
      if (!this.item.point_used) return 0;
      return Math.min(100, (this.progressValue / this.item.point_used) * 100);
    },
    remainingLabel() {
      if (this.outOfStock)
        return `${this.$t("remain")} : 0 ${this.$t("right_unit")}`;
      if (this.item.max_quantity) {
        const remain = Math.max(
          0,
          Number(this.item.max_quantity || 0) -
            Number(this.item.quantity_used || 0)
        );
        return `${this.$t("remain")} : ${remain} ${this.$t("right_unit")}`;
      }
      return `${this.$t("remain")} : -`;
    }
  },
  methods: {
    stripHtml(value) {
      return value ? String(value).replace(/(<([^>]+)>)/gi, "") : "";
    }
  }
};
</script>

<template>
  <article class="crm-reward-card" :data-test="dataTest" @click="$emit('select', item)">
    <div class="crm-reward-card__media">
      <c-img-lazy :src="image" class="crm-reward-card__image" />
    </div>
    <div class="crm-reward-card__body">
      <h3 class="crm-reward-card__title">{{ item.product_name }}</h3>
      <p class="crm-reward-card__description">
        {{ stripHtml(item.description) }}
      </p>
      <div class="crm-reward-card__points">
        <font-awesome-icon :icon="['fas', 'gift']" />
        {{ numeralFormat(item.point_used || 0) }}
      </div>
      <div class="crm-reward-card__progress">
        <span
          :class="['crm-reward-card__progress-bar', { 'is-empty': outOfStock }]"
          :style="{ width: progressPercent + '%' }"
        ></span>
      </div>
      <div class="crm-reward-card__meta">
        <span :class="{ 'is-low': outOfStock }">{{ remainingLabel }}</span>
        <strong>{{ numeralFormat(progressValue) }}/{{ numeralFormat(item.point_used || 0) }}</strong>
      </div>
    </div>
  </article>
</template>

<style lang="scss" scoped>
.crm-reward-card {
  display: grid;
  grid-template-columns: 34% minmax(0, 1fr);
  overflow: hidden;
  min-height: 118px;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  cursor: pointer;
}

.crm-reward-card__media {
  background: #f4f4f4;
}

.crm-reward-card__image {
  width: 100%;
  height: 100%;
}

.crm-reward-card__image :deep(.v-img) {
  height: 100%;
}

.crm-reward-card__body {
  display: flex;
  min-width: 0;
  flex-direction: column;
  padding: 10px 12px;
}

.crm-reward-card__title {
  display: -webkit-box;
  overflow: hidden;
  margin: 0;
  font-size: var(--text-lg);
  font-weight: 800;
  line-height: 1.35;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.crm-reward-card__description {
  display: -webkit-box;
  overflow: hidden;
  margin: 2px 0 6px;
  color: #a0a0a0;
  font-size: var(--text-sm);
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
}

.crm-reward-card__points {
  display: inline-flex;
  width: fit-content;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  background: #f7dbe2;
  color: var(--primary-color);
  font-size: var(--text-md);
  font-weight: 800;
}

.crm-reward-card__progress {
  height: 5px;
  overflow: hidden;
  margin-top: auto;
  border-radius: 999px;
  background: #d9d9d9;
}

.crm-reward-card__progress-bar {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: var(--primary-color);
}

.crm-reward-card__progress-bar.is-empty {
  background: #bdbdbd;
}

.crm-reward-card__meta {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: 5px;
  color: #b1b1b1;
  font-size: var(--text-sm);
}

.crm-reward-card__meta strong,
.crm-reward-card__meta .is-low {
  color: var(--primary-color);
}
</style>
